import React, { useContext, useState } from 'react';
import {
  StyleSheet, View, Text,
} from 'react-native';
import { CheckBox } from '@ui-kitten/components';
import _ from 'lodash';
import moment from 'moment';
import { observer } from 'mobx-react-lite';
import '../../style/DayPicker.css';

import { Call } from '../../types';
import Style from '../../style';
import I18n, { formatDate } from '../../i18n';
import { getCallEventIcon } from '../../util/helpers';
import { Log } from '../../types/Calls';
import { RootStoreContext } from '../../stores/RootStore';
import { dialInfoAccess } from '../../navigation/AccessRules';
import { fetchUser } from '../../api/Users';

interface CallLogsProps {
  callDetails: Call;
}
const CallLogs = observer(({ callDetails }: CallLogsProps) => {
  const store = useContext(RootStoreContext);
  const [includeDialInfo, setIncludeDialInfo] = useState(false);
  const { data: client } = fetchUser(callDetails.clientId).swr;
  const { data: coach } = fetchUser(callDetails.coachId).swr;
  if (!callDetails.logs?.length) return null;
  const logsSortedByTime = _.sortBy(callDetails.logs, 'time').reverse();
  const canViewDialInfo = store.auth.checkAccess(dialInfoAccess);
  const showDialInfo = canViewDialInfo && includeDialInfo;

  const isDialInfo = (log: Log) => (
    log.event === 'RegisterCaller' || log.event === 'DeregisterCaller'
  );

  const getNoteOrReason = (log: Log) => {
    const reason = log.reason
      ? I18n.t(`ui.calls.${log.reason}`, { defaultValue: log.reason })
      : '';

    if (reason && log.notes) return `${reason} (${log.notes})`;
    return reason || log.notes || '';
  };

  const getNewCallTime = (log: Log) => {
    if (log.event !== 'Move' && log.event !== 'Schedule') return '';
    const newTime = log.data?.start || log.start;
    if (!newTime || !moment(newTime).isValid()) return '';
    return formatDate('date.appointmentDateTime', newTime);
  };

  const getDialInfo = (log: Log) => {
    const caller = [client, coach].find((user) => user?.id === log.userId);
    const firstName = caller?.firstName || '';
    const code = caller?.code ? `[${caller.code}]` : '';
    return [firstName, code].filter(Boolean).join(' ');
  };

  const renderLog = (log: Log, index: number) => {
    const actionTime = log.time && moment(log.time).isValid()
      ? formatDate('date.appointmentDateTime', log.time) : '';
    const action = log.event
      ? I18n.t(`ui.calls.event.${log.event}`, { defaultValue: log.event }) : '';
    const reason = isDialInfo(log) ? getDialInfo(log) : getNoteOrReason(log);
    const icon = getCallEventIcon(log.event || '');

    return (
      <View key={`${log.time?.toString() || 'log'}-${log.event || ''}-${index}`} style={styles.logRow}>
        <View style={styles.actionTimeCell}>
          <Style.Icon.Note width={24} height={24} fill={Style.Color.Primary} />
          <Text style={styles.infoElementText}>
            {actionTime}
          </Text>
        </View>
        <Text style={styles.separator}>|</Text>
        <View style={styles.actionCell}>
          {!!log.event && (
            <Style.Icon.ByName name={icon} width={16} height={16} fill={Style.Color.Gray400} />
          )}
          <Text style={styles.actionText}>{action}</Text>
        </View>
        <Text style={styles.separator}>|</Text>
        <Text style={styles.newTimeCell}>{getNewCallTime(log)}</Text>
        <Text style={styles.separator}>|</Text>
        <Text style={styles.reasonCell}>{reason}</Text>
      </View>
    );
  };

  return (
    <View key={callDetails.id}>
      <View style={styles.itemDivider} />
      {canViewDialInfo && (
        <CheckBox
          style={styles.dialInfoCheckbox}
          checked={includeDialInfo}
          onChange={setIncludeDialInfo}
        >
          {I18n.t('ui.calls.includeDialInfo')}
        </CheckBox>
      )}
      {logsSortedByTime.filter((log) => showDialInfo || !isDialInfo(log)).map((log, index) => (
        renderLog(log, index)
      ))}
    </View>
  );
});

export default CallLogs;

const styles = StyleSheet.create({
  logRow: {
    flexDirection: 'row',
    marginHorizontal: 28,
    marginBottom: 15,
    alignItems: 'center',
  },
  actionTimeCell: {
    flex: 1.35,
    flexDirection: 'row',
    alignItems: 'center',
  },
  infoElementText: {
    ...Style.Text.Normal,
    color: Style.Color.Gray800,
    marginLeft: 15,
  },
  separator: {
    ...Style.Text.Normal,
    color: Style.Color.Gray200,
    marginHorizontal: 10,
  },
  actionCell: {
    flex: 1.15,
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionText: {
    ...Style.Text.Normal,
    color: Style.Color.Gray400,
    marginLeft: 5,
  },
  newTimeCell: {
    ...Style.Text.Normal,
    color: Style.Color.Gray800,
    flex: 1.1,
  },
  reasonCell: {
    ...Style.Text.NormalItalic,
    color: Style.Color.Gray800,
    flex: 1,
  },
  itemDivider: {
    borderBottomColor: Style.Color.Gray200,
    borderBottomWidth: 1,
    marginHorizontal: 28,
    marginTop: 10,
    marginBottom: 25,
  },
  dialInfoCheckbox: {
    marginHorizontal: 28,
    marginBottom: 20,
  },
});
